import { useCallback, useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient, useIsMutating, type QueryClient } from '@tanstack/react-query'
import type { ContactDuplicates, Lead, SfAccount } from '@/shared/types'
import { apiFetch, type LeadsResponse } from './core'
import { afterLeadWrite } from './dealCache'
import {
  addLeadToTables, dropLeadFromFocus, refetchFocusLists, refreshLeadInLists, spliceLeadIntoCache,
} from './leadLists'
import { trackEvent } from './telemetry'
import { topUpMeta } from './trackMeta'
import { FOCUS_POLL_JITTER_MS, FOCUS_POLL_MS, jitteredInterval } from './focusPoll'
import { TEST_QUEUE } from '@/shared/routes'

export type LeadSegment = 'focus' | 'all'

/** What a lead list asks the server for. One per cache entry. */
export interface LeadListParams {
  segment: LeadSegment
  search?: string
  // The stage filter applies to both segments so the BDR's choice carries
  // across views.
  includeStages?: readonly string[]
  // Focus only: also return leads promised beyond the lookahead and ones the
  // seller has postponed, so the queue can show the whole book on request.
  showAll?: boolean
  // Focus only, admin only: whose queue to render. Server-gated on manage_users.
  asUser?: string | null
  // Focus only, admin only: the test queue of QA test leads instead of a
  // seller's. Server-gated on manage_users.
  testQueue?: boolean
}

// How long a list nobody renders stays cached. React Query's default of five
// minutes dropped Tabell's list during an ordinary stint in Focus, and the next
// visit drew the skeleton for the 1-5 s the list takes to load. Kept for a
// working session instead, so a revisit renders the cached rows at once and
// refetches behind them; lead writes keep patching the cached rows meanwhile.
export const LEAD_LIST_GC_MS = 60 * 60_000

/** The cache key and request for one lead list. */
export function leadListRequest({
  segment, search = '', includeStages = [], showAll = false, asUser = null, testQueue = false,
}: LeadListParams) {
  const q = search.trim()
  const params = new URLSearchParams()
  if (segment === 'focus') params.set('segment', 'focus')
  if (q) params.set('search', q)
  if (segment === 'focus' && showAll) params.set('showAll', 'true')
  if (segment === 'focus' && testQueue) params.set('testQueue', 'true')
  else if (segment === 'focus' && asUser) params.set('asUser', asUser)
  for (const s of includeStages) params.append('include_stage', s)
  // Sort the include list for a stable cache key — order doesn't matter to
  // the server, but unordered arrays would mean every render is a new key.
  const stagesKey = [...includeStages].sort().join(',')
  // Which queue: a seller's email, TEST_QUEUE, or null for your own.
  const queue = segment === 'focus' && testQueue ? TEST_QUEUE : asUser
  return {
    queryKey: ['leads', segment, q, stagesKey, showAll, queue] as const,
    path: `/api/leads?${params.toString()}`,
  }
}

/** Rows in Tabell's first page: a few screens, so the seller can scroll a
 *  little before the full list arrives. */
export const FIRST_PAGE_ROWS = 200

/**
 * The first rows of a table list, in the order Tabell shows them (YCRM-83).
 * `sort` is firstPageSortParam's. The key extends the full list's, so writes
 * that patch cached table lists patch this one too.
 */
export function firstPageRequest(list: LeadListParams, sort: string) {
  const { queryKey, path } = leadListRequest(list)
  return {
    queryKey: [...queryKey, 'first', sort] as const,
    path: `${path}&limit=${FIRST_PAGE_ROWS}&sort=${encodeURIComponent(sort)}`,
  }
}

/** Tabell's first page, fetched beside the full list on a first visit. */
export function useLeadsFirstPage(list: LeadListParams, sort: string | null, enabled: boolean) {
  const { queryKey, path } = firstPageRequest(list, sort ?? '')
  return useQuery({
    queryKey,
    queryFn: () => apiFetch<LeadsResponse>(path),
    enabled: enabled && sort !== null,
    staleTime: 30_000,
  })
}

// Focus = BDR's actionable queue (server also hides postponed leads + flags
// skipped). Table = full pipeline view.
export function useLeads(
  list: LeadListParams,
  // `poll`: re-read a Focus queue every minute, for the views that show it.
  { enabled = true, poll = true }: { enabled?: boolean; poll?: boolean } = {},
) {
  const qc = useQueryClient()
  const pendingWrites = useIsMutating({ predicate: mutation => mutation.options.mutationKey?.[0] !== 'presence' })
  // Picked once per mount, so each seller keeps a steady interval of their own.
  const [pollMs] = useState(() => jitteredInterval(FOCUS_POLL_MS, FOCUS_POLL_JITTER_MS))
  const { segment } = list
  const { queryKey, path } = leadListRequest(list)
  const query = useQuery({
    queryKey,
    queryFn: () => apiFetch<LeadsResponse>(path),
    enabled,
    staleTime: 30_000,
    gcTime: LEAD_LIST_GC_MS,
    // Hidden callbacks and expired skips cannot return from a local clock
    // alone: they need a fresh server snapshot. Pause polling during writes
    // so an older snapshot cannot replace an optimistic edit.
    refetchInterval: segment === 'focus' && poll && pendingWrites === 0 ? pollMs : false,
    // One more than the app default, to ride out a short burst of 503s before
    // the "Kunde inte uppdatera listan" notice shows over the loaded queue.
    retry: 2,
    refetchOnWindowFocus: () => segment === 'focus' && qc.isMutating({ predicate: mutation => mutation.options.mutationKey?.[0] !== 'presence' }) === 0,
  })
  // A lead write cancels every list fetch in flight (useLeadMutations), and a
  // cancelled first load leaves the list with no data and no fetch. Tabell's
  // first page lets a seller edit a row before the full list has arrived, so
  // start the full list again once the writes have settled.
  const stalled = segment === 'all' && enabled && query.status === 'pending' && query.fetchStatus === 'idle'
  const { refetch } = query
  useEffect(() => {
    if (stalled && pendingWrites === 0) void refetch()
  }, [stalled, pendingWrites, refetch])
  return query
}

export function useLead(companyId: string | null) {
  return useQuery({
    queryKey: ['lead', companyId],
    queryFn: () => apiFetch<Lead>(`/api/leads/${companyId}`),
    enabled: !!companyId,
    staleTime: 30_000,
  })
}

// Other leads whose contacts look like this lead's contacts — the same human
// reached through two companies. Its own request rather than a field on Lead:
// it costs a second pass over the contacts table and the card must not wait on
// it, so the flag appears a moment after the rest of the header.
export function useContactDuplicates(companyId: string | null) {
  return useQuery({
    queryKey: ['contact-duplicates', companyId],
    queryFn: () => apiFetch<ContactDuplicates>(`/api/leads/${companyId}/contact-duplicates`),
    enabled: !!companyId,
    staleTime: 60_000,
  })
}

/**
 * What Salesforce says about this lead right now.
 *
 * Its own request rather than fields on `Lead`: the list endpoints carry a
 * copy synced earlier (`sfStatus`, `sfLeadOwner`, …), and this is the live
 * read — plus `ownerKind`, which is the difference between "a colleague owns
 * this" and "it is sitting in a shared queue". The card renders the synced
 * copy until this resolves, and keeps rendering it if this fails.
 */
export interface LeadSalesforce {
  /** False when Salesforce isn't connected at all — distinct from `found`,
   *  which is only meaningful once this is true. */
  configured: boolean
  found: boolean
  /** False when missing company identity prevented a Salesforce lookup. */
  checked?: boolean
  sfId: string | null
  status: string | null
  owner: string | null
  ownerKind: 'user' | 'queue' | null
  rating: string | null
  lastActivityAt: string | null
  convertedAt: string | null
  openOpportunityStage?: string | null
  link: string | null
  /** The company's Account, read live; null without one. */
  account?: (SfAccount & { id: string }) | null
  /** Set when the Account read failed; `account` is then null and unknown. */
  accountsError?: string | null
}

export function useLeadSalesforce(companyId: string | null) {
  return useQuery({
    queryKey: ['lead-salesforce', companyId],
    queryFn: () => apiFetch<LeadSalesforce>(`/api/leads/${companyId}/salesforce`),
    enabled: !!companyId,
    staleTime: 60_000,
    // Unconfigured or unreachable Salesforce is a normal state for this
    // panel, not something to hammer.
    retry: false,
  })
}

// Warm the single-lead cache for the next lead in the focus queue so
// flipping through the queue never waits on the detail fetch.
export function usePrefetchLead() {
  const qc = useQueryClient()
  return useCallback((companyId: string) =>
    qc.prefetchQuery({
      queryKey: ['lead', companyId],
      queryFn: () => apiFetch<Lead>(`/api/leads/${companyId}`),
      staleTime: 30_000,
    }), [qc])
}

// ── mutations ──────────────────────────────────────────────────────────────

interface UpdateLeadVars {
  id: string
  patch: Omit<Partial<Lead>, 'nextContactAt'> & { nextContactAt?: string | null }
}

// PATCH /api/leads/{id} — covers stage / seller / meeting / comment /
// next-contact / disqualified-reason / got-contact / booked-meeting / feedback.
// Splices the server's response Lead into the cache so server-derived fields
// (handover.primary after primaryContactOverride, recomputed stage pills,
// etc.) update on the PATCH response (~150ms) rather than waiting for an
// invalidate-triggered refetch (~600ms).
export function useUpdateLead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, patch }: UpdateLeadVars) =>
      apiFetch<Lead>(`/api/leads/${id}`, { method: 'PATCH', body: patch }),
    onSuccess: (lead) => {
      spliceLeadIntoCache(qc, lead)
      afterLeadWrite(qc, lead.id)
      qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

// Focus listens for this key to refill the queue after an outcome is saved.
export const SAVE_OUTCOME_KEY = 'saveOutcome'

export function useSaveOutcome() {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: [SAVE_OUTCOME_KEY],
    mutationFn: ({ id, eventId, patch, event }: {
      id: string; eventId: string; patch: Partial<Lead>
      event: { kind: string; outcome?: string; body?: string }
    }) => apiFetch<Lead>(`/api/leads/${id}/events`, {
      method: 'POST', body: { ...event, id: eventId, leadPatch: patch },
    }),
    onSuccess: lead => {
      spliceLeadIntoCache(qc, lead)
      qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

// Retract a timeline entry logged by mistake. Appends a correction rather than
// deleting — the timeline is append-only because a read-append-write drops
// concurrent entries — and unwinds any booking state the entry had set.
export interface CorrectEntryVars {
  leadId: string
  entryId: string
  reason?: string
}

export function useCorrectEntry() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ leadId, entryId, reason }: CorrectEntryVars) =>
      apiFetch<{ changed: Record<string, unknown>; wasBooking: boolean }>(
        `/api/leads/${leadId}/corrections`,
        { method: 'POST', body: { entryId, reason } },
      ),
    onSuccess: (_res, vars) => {
      // The lead's stage and booking flags may have moved, and the dashboard
      // counts bookings off the timeline. The response is the change set, not
      // the lead, so the lead is refetched on its own and patched into the
      // lists. Undoing a booking can reopen the lead into its owner's queue.
      void refreshLeadInLists(qc, vars.leadId).then(() => refetchFocusLists(qc))
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      afterLeadWrite(qc, vars.leadId)
    },
  })
}

export interface CreateLeadInput {
  company: string
  orgNo?: string
  city?: string
  country?: string
  employees?: number
  website?: string
  stage?: string
  contact?: { name: string; role?: string; email?: string; phone?: string; linkedin?: string }
  /** Create it even though a company with the same name and country exists. */
  allowDuplicate?: boolean
}

export interface CreateLeadResult {
  // false = a company with this org number was already in the CRM; `lead` is
  // the existing one (its working state was left untouched).
  created: boolean
  lead: Lead
}

// POST /api/leads — manually add a lead from the UI (gated by edit_contacts).
// Idempotent on org number. A new lead joins the cached table lists, and the
// single-lead cache is primed so opening it is instant. Focus refetches because
// weighted allocation may hand the new lead to a seller's queue.
export function useCreateLead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateLeadInput) =>
      apiFetch<CreateLeadResult>('/api/leads', { method: 'POST', body: input }),
    onSuccess: ({ lead, created }) => {
      spliceLeadIntoCache(qc, lead)
      if (created) addLeadToTables(qc, lead)
      void refetchFocusLists(qc)
      qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

interface LogEventVars {
  id: string
  event: {
    kind: string
    body?: string
    outcome?: string
    payload?: Record<string, unknown>
    /** With an id the server writes the entry once, however often it is sent. */
    id?: string
    /** Lead fields that commit in the same request as the entry. */
    leadPatch?: Partial<Lead>
  }
}

// POST /api/leads/{id}/events — append a timeline entry. Server stamps
// `at` and `by`. Side-effects per kind ('call' bumps stage, 'meeting' sets
// meeting_at + Booked, 'handover' sets seller + Handed Over). The PATCH
// response is the refreshed Lead; splice it in so the canonical entry
// (with the server-stamped `by` field instead of the optimistic 'me')
// shows up without waiting for a refetch.
export function useLogEvent() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, event }: LogEventVars) =>
      apiFetch<Lead>(`/api/leads/${id}/events`, { method: 'POST', body: event }),
    onSuccess: (lead) => {
      spliceLeadIntoCache(qc, lead)
      afterLeadWrite(qc, lead.id)
      qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

// POST /api/leads/{id}/skip — hide the lead from THIS BDR's Focus queue.
// `days=0` (default) is Skip — hidden until end of today (Europe/Stockholm).
// `days>0` is Postpone — hidden for N days. Neither writes a timeline event,
// so dashboard call/meeting counters are unaffected.
interface SkipVars { id: string; days?: number }
export function useSkipLead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, days = 0 }: SkipVars) =>
      apiFetch<Lead>(`/api/leads/${id}/skip`, {
        method: 'POST',
        body: { days },
      }),
    onSuccess: (lead) => {
      spliceLeadIntoCache(qc, lead)
      qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

// ── Kontakta ej ─────────────────────────────────────────────────────────────

export const DO_NOT_CONTACT_ADMIN_KEY = ['admin', 'do-not-contact'] as const

/**
 * After the mark is set or lifted. Tabell's row is patched in place. A marked
 * lead leaves Focus straight from the cache, since no queue may hold it; an
 * unmarked one can rejoin its owner's queue, which only the server can say, so
 * Focus refetches.
 */
export function afterDoNotContactWrite(qc: QueryClient, lead: Lead): void {
  spliceLeadIntoCache(qc, lead)
  if (lead.doNotContact) dropLeadFromFocus(qc, lead.id)
  else void refetchFocusLists(qc)
  void qc.invalidateQueries({ queryKey: DO_NOT_CONTACT_ADMIN_KEY })
  void qc.invalidateQueries({ queryKey: ['dashboard'] })
}

interface DoNotContactVars { id: string; reason?: string }

// Idempotent on the server: marking a marked lead returns it unchanged.
export function useMarkDoNotContact() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: DoNotContactVars) =>
      apiFetch<Lead>(`/api/leads/${id}/do-not-contact`, { method: 'POST', body: { reason } }),
    onSuccess: lead => afterDoNotContactWrite(qc, lead),
  })
}

// manage_users only; anyone else gets a 403.
export function useRemoveDoNotContact() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: DoNotContactVars) =>
      apiFetch<Lead>(`/api/leads/${id}/do-not-contact`, { method: 'DELETE', body: { reason } }),
    onSuccess: lead => afterDoNotContactWrite(qc, lead),
  })
}

// ── Ownership ───────────────────────────────────────────────────────────────

/**
 * After a claim, release, transfer or pull. Ownership does not decide which
 * leads Tabell lists, so its rows are patched; it decides every Focus queue,
 * so those refetch, and the shared pool's count changed with it.
 */
export function afterOwnershipWrite(qc: QueryClient, leads: Lead[]): void {
  for (const lead of leads) {
    spliceLeadIntoCache(qc, lead)
    afterLeadWrite(qc, lead.id)
  }
  void refetchFocusLists(qc)
  void qc.invalidateQueries({ queryKey: ['pool'] })
}

// `claimed: false` is a normal outcome, not an error — the lead is already
// someone else's and `lead.assignedTo` says who.
export interface ClaimResult {
  claimed: boolean
  lead: Lead | null
}

// Pulling from the shared pool. An empty `leads` with poolEmpty=false means
// other sellers held the rows for an instant, not that there is no work — the
// two must not collapse into one message, or a busy queue looks broken.
export interface ClaimNextResult {
  leads: Lead[]
  poolEmpty: boolean
}

// How many leads the caller could pull right now. Short staleTime: the pool is
// shared, so this goes out of date as colleagues take from it.
export function usePool(enabled = true) {
  return useQuery({
    queryKey: ['pool'],
    queryFn: () => apiFetch<{ available: number }>('/api/leads/pool'),
    enabled,
    staleTime: 15_000,
  })
}

export function useClaimLead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<ClaimResult>(`/api/leads/${id}/claim`, { method: 'POST' }),
    onSuccess: (res) => {
      // Splice on either outcome: losing the race still tells us who the real
      // owner is, and surfacing that immediately is the point.
      if (res.claimed && res.lead) afterOwnershipWrite(qc, [res.lead])
      else if (res.lead) spliceLeadIntoCache(qc, res.lead)
    },
  })
}

// Splitting the leads: top this seller up to the target queue size from the
// shared pool. Atomic underneath, so two sellers topping up at once take
// disjoint sets. Idempotent — calling it when already at target does nothing.
export interface TopUpResult {
  claimed: Lead[]
  /** Leads this seller can call right now — what `target` is measured against. */
  owned: number
  target: number
  /** Their whole open book, closed stages excluded — what `maxOpen` bounds. */
  openOwned: number
  maxOpen: number
  /** Callable leads they hold that nobody has worked yet, and the floor it is topped up to. */
  fresh: number
  freshFloor: number
  poolEmpty: boolean
  /**
   * Why the pull stopped. `poolEmpty` alone could not tell "you are at target"
   * from "you are holding too much" from "a colleague got there first", and
   * those need different things from the seller.
   */
  stopReason: 'target' | 'ceiling' | 'poolEmpty' | 'contended' | 'autoOff'
}

// Focus skips a refill while a top-up with this key is in flight.
export const TOP_UP_KEY = 'topUp'

/** `silent` leaves the pull out of top_up_result: the refill while selling. */
export type TopUpOptions = { silent?: boolean } | void

export function useTopUp() {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: [TOP_UP_KEY],
    mutationFn: (_opts: TopUpOptions) => apiFetch<TopUpResult>('/api/leads/top-up', { method: 'POST' }),
    onError: () => trackEvent('write_failed', { op: 'top_up' }),
    // Tracked on the hook rather than at either button, because the top-up that
    // matters most is the automatic one on Focus mount — the pull whose empty
    // result is what "why did I get no leads" is actually asking about. The
    // server already works out why it stopped; this is the record of it. The
    // refills are silent: one per saved outcome would drown that record.
    onSuccess: (res, opts) => {
      if (!opts?.silent) trackEvent('top_up_result', topUpMeta(res))
      if (res.claimed.length === 0) return
      afterOwnershipWrite(qc, res.claimed)
    },
  })
}

export function useClaimNext() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (count: number = 1) =>
      apiFetch<ClaimNextResult>(`/api/leads/claim-next?count=${count}`, { method: 'POST' }),
    // Newly claimed leads were not in this client's focus list at all, so a
    // splice cannot introduce them; afterOwnershipWrite refetches Focus.
    onSuccess: (res) => afterOwnershipWrite(qc, res.leads),
  })
}

/**
 * Hand a lead to a named colleague.
 *
 * `body` is the object, not a JSON string — apiFetch stringifies it, so
 * pre-serializing would send a quoted string and the route would 422.
 *
 * Refetches the Focus lists: the lead leaves the sender's queue and enters the
 * recipient's, so a stale list would keep offering them a lead that is no
 * longer theirs to call.
 */
export function useTransferLead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, to }: { id: string; to: string }) =>
      apiFetch<Lead>(`/api/leads/${id}/transfer`, { method: 'POST', body: { to } }),
    onSuccess: (lead) => afterOwnershipWrite(qc, [lead]),
  })
}

export function useReleaseLead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<Lead>(`/api/leads/${id}/release`, { method: 'POST' }),
    // Releasing puts the lead back where colleagues can take it.
    onSuccess: (lead) => afterOwnershipWrite(qc, [lead]),
  })
}
